import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Edit2, ClipboardCheck, Archive, Trash2, Calendar } from "lucide-react";

export default function AdminYouthDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const load = () => api.get(`/youth/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Erro"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="text-slate-400">Carregando...</div>;

  const del = async () => {
    if (!confirm(`EXCLUIR permanentemente ${data.full_name}? Prefira arquivar.`)) return;
    if (!confirm("Esta ação não pode ser desfeita. Confirmar exclusão?")) return;
    await api.delete(`/youth/${id}`);
    toast.success("Excluído");
    nav("/admin/jovens");
  };

  const archive = async () => {
    if (!confirm("Arquivar? O histórico será preservado.")) return;
    await api.post(`/youth/${id}/archive`);
    toast.success("Arquivado");
    nav("/admin/jovens");
  };

  const removeAttendance = async (aid) => {
    if (!confirm("Remover este registro de presença?")) return;
    await api.delete(`/attendance/${aid}`);
    toast.success("Presença removida");
    load();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => nav("/admin/jovens")} className="text-xs text-slate-400 hover:text-blue-400 inline-flex items-center gap-1"><ArrowLeft size={12}/> Voltar</button>

      <div className="ci-card p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center font-display font-bold text-blue-300 text-xl shrink-0">
            {data.full_name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-black">{data.full_name}</h1>
            <div className="text-sm text-slate-400 mt-1 flex gap-2 flex-wrap">
              {data.age != null && <span>{data.age} anos</span>}
              <span>· {data.age_bucket}</span>
              {data.church && <span>· {data.church}</span>}
              <span>· {data.attendance_count} presença(s)</span>
            </div>
            {!data.is_complete && (
              <div className="mt-2 inline-block text-[10px] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full">CADASTRO INCOMPLETO</div>
            )}
            {data.status === "archived" && (
              <div className="mt-2 inline-block text-[10px] font-bold text-slate-300 bg-slate-500/10 border border-slate-500/30 px-2 py-0.5 rounded-full ml-2">ARQUIVADO</div>
            )}
          </div>
        </div>
        <div className="mt-6 flex gap-2 flex-wrap">
          <button data-testid="youth-detail-edit" onClick={() => nav(`/admin/jovens/${id}/editar`)} className="btn-ghost text-xs"><Edit2 size={14}/> Editar</button>
          <button onClick={() => nav(`/admin/presenca?youth=${id}`)} className="btn-primary text-xs"><ClipboardCheck size={14}/> Registrar presença</button>
          <button onClick={archive} className="btn-ghost text-xs border-amber-500/30 text-amber-300"><Archive size={14}/> Arquivar</button>
          <button onClick={del} className="btn-ghost text-xs border-red-500/30 text-red-300"><Trash2 size={14}/> Excluir</button>
        </div>
      </div>

      <div className="ci-card p-6">
        <div className="label mb-4">Dados cadastrados</div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          {[
            ["Nascimento", data.birth_date?.slice(0,10)],
            ["Cidade / UF", [data.city, data.state].filter(Boolean).join(" / ")],
            ["WhatsApp", data.whatsapp],
            ["Telefone", data.phone],
            ["E-mail", data.email],
            ["Preferência", data.preferred_contact],
            ["Autoriza contato", data.can_contact ? "Sim" : "Não"],
            ["Igreja", data.church],
            ["Ministério", data.ministry],
            ["1ª visita", data.first_visit_date?.slice(0,10)],
            ["Visitante 1ª vez", data.is_first_timer ? "Sim" : "Não"],
            ["Cadastrado em", data.created_at?.slice(0,10)],
          ].map(([l, v]) => (
            <div key={l} className="border-b border-white/5 pb-2">
              <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold">{l}</div>
              <div className="text-white mt-1">{v || "—"}</div>
            </div>
          ))}
        </div>
        {data.notes && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold">Observações</div>
            <p className="text-slate-300 mt-1 text-sm whitespace-pre-wrap">{data.notes}</p>
          </div>
        )}
      </div>

      <div className="ci-card p-6">
        <div className="label mb-4">Histórico de presenças</div>
        {(data.attendance_history || []).length === 0 ? (
          <div className="text-sm text-slate-500">Nenhuma presença registrada ainda.</div>
        ) : (
          <div className="space-y-2">
            {data.attendance_history.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 bg-black/40 border border-white/5 rounded-lg px-3 py-2 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <Calendar size={14} className="text-blue-400 shrink-0"/>
                  <div className="min-w-0">
                    <div className="truncate">{a.event_name}</div>
                    <div className="text-xs text-slate-500">{a.event_date?.slice(0,10)} · {a.event_type}</div>
                  </div>
                </div>
                <button onClick={() => removeAttendance(a.id)} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 size={14}/></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
